import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Trophy, Users, Gamepad2 } from 'lucide-react';
import {
  fetchFriendsLeaderboard, fetchGameLeaderboard,
  type LeaderboardEntry, type GameLeaderboardEntry,
} from '@/lib/api';
import { allThemeGames } from '@/lib/themeGames';
import NamedIcon from '@/components/ui/NamedIcon';
import styles from './FriendsLeaderboardPage.module.css';

interface Props { onNavigate: (path: string) => void }

/**
 * Friend leaderboard for the current week. Ranks self + accepted
 * friends by kWh; the driver's own row highlights even if they
 * haven't charged yet so the "where do I stand" answer is always
 * visible.
 *
 * 60s `refetchInterval` keeps the rank live as the 15-min cron
 * snapshot updates — matches the spec's "drivers see their rank
 * shift within 15 minutes" promise with a tighter client poll.
 */
export function FriendsLeaderboardPage({ onNavigate }: Props) {
  const { t } = useTranslation('leaderboard');

  return (
    <div className={styles.page}>
      <button className={styles.backFab} onClick={() => onNavigate('/friends')} aria-label={t('friends.back')}>
        <ArrowLeft size={18} />
      </button>

      <header className={styles.header}>
        <div className={styles.headerIcon}>
          <NamedIcon name="trophy" fallback={<Trophy size={20} />} size={20} />
        </div>
        <div className={styles.headerText}>
          <h1 className={styles.title}>{t('friends.title')}</h1>
          <p className={styles.subtitle}>
            {t('friends.subtitle')}
          </p>
        </div>
      </header>

      <FriendsLeaderboardTabs />
    </div>
  );
}

/**
 * Segmented host for the two friends boards: weekly kWh and game
 * scores. One Leaderboard surface, two rankings — mounted both by
 * the standalone page above and by the Friends page's Leaderboard
 * tab. Defaults to the kWh week board; the toggle is local state
 * only (not a URL slot, same reasoning as the Friends tabs).
 */
export function FriendsLeaderboardTabs() {
  const { t } = useTranslation('leaderboard');
  const [board, setBoard] = useState<'week' | 'games'>('week');

  return (
    <>
      <div className={styles.segmented} role="tablist">
        <button
          role="tab"
          className={`${styles.segBtn} ${board === 'week' ? styles.segBtnActive : ''}`}
          onClick={() => setBoard('week')}
          aria-selected={board === 'week'}
        >
          {t('friends.toggleWeek')}
        </button>
        <button
          role="tab"
          className={`${styles.segBtn} ${board === 'games' ? styles.segBtnActive : ''}`}
          onClick={() => setBoard('games')}
          aria-selected={board === 'games'}
        >
          {t('friends.toggleGames')}
        </button>
      </div>
      {board === 'week' ? <FriendsLeaderboardList /> : <GameLeaderboardList />}
    </>
  );
}

/**
 * The ranked list on its own — query + empty hint + rows, no page
 * chrome. Shared between the standalone /leaderboard/friends page
 * above and the Leaderboard tab on the Friends page, so the two
 * surfaces can never drift. Data fetching lives here (not in the
 * host), which also means the 60s poll only runs while a host
 * actually has the list mounted.
 */
export function FriendsLeaderboardList() {
  const { t } = useTranslation('leaderboard');
  const q = useQuery({
    queryKey: ['leaderboard', 'friends'],
    queryFn: fetchFriendsLeaderboard,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  if (q.isLoading && !q.data) {
    return <p className={styles.loading}>{t('friends.loading')}</p>;
  }
  if (!q.data) return null;
  const entries = q.data.entries;

  return (
    <>
      {entries.length === 1 && (
        <div className={styles.emptyHint}>
          <Users size={28} className={styles.emptyHintIcon} />
          <p className={styles.emptyHintTitle}>{t('friends.emptyTitle')}</p>
          <p className={styles.emptyHintBody}>
            {t('friends.emptyBody')}
          </p>
        </div>
      )}

      <div className={styles.list}>
        {entries.map((e) => (
          <Row key={e.userId} entry={e} />
        ))}
      </div>
    </>
  );
}

/**
 * Friends game leaderboard: total points with per-game points summaries.
 * Game points are NOT EVI Points (bragging rights only, no
 * conversion); the ranking is total points, the meta line carries
 * the two strongest games by points, using names from the games table.
 */
export function GameLeaderboardList() {
  const { t } = useTranslation('leaderboard');
  const q = useQuery({
    queryKey: ['leaderboard', 'games'],
    queryFn: fetchGameLeaderboard,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  if (q.isLoading && !q.data) {
    return <p className={styles.loading}>{t('friends.loading')}</p>;
  }
  if (!q.data) return null;
  const entries = q.data.entries;
  const nobodyPlayed = entries.every((e) => e.totalPoints === 0
    && Object.keys(e.gamePoints ?? {}).length === 0);

  return (
    <>
      {nobodyPlayed && (
        <div className={styles.emptyHint}>
          <Gamepad2 size={28} className={styles.emptyHintIcon} />
          <p className={styles.emptyHintTitle}>{t('friends.gamesEmptyTitle')}</p>
          <p className={styles.emptyHintBody}>
            {t('friends.gamesEmptyBody')}
          </p>
        </div>
      )}

      <div className={styles.list}>
        {entries.map((e) => (
          <GameRow key={e.userId} entry={e} />
        ))}
      </div>
    </>
  );
}

/** Game display name for a leaderboard ns — from the games table, so
 *  a new game's name appears here with no leaderboard change. Falls
 *  back to the raw ns for a server-known game the bundle predates. */
function gameNameForNs(ns: string): string {
  return allThemeGames().find((g) => g.ns === ns)?.name ?? ns;
}

function GameRow({ entry }: { entry: GameLeaderboardEntry }) {
  const { t } = useTranslation('leaderboard');
  const medal = entry.rank === 1 ? '🥇'
    : entry.rank === 2 ? '🥈'
    : entry.rank === 3 ? '🥉'
    : `#${entry.rank}`;
  const isPodium = entry.rank <= 3;
  const initial = entry.displayName.slice(0, 1).toUpperCase();

  // The row's two strongest games by points — bounded width whatever
  // the game count grows to. Titles are proper nouns from the games
  // table (ns-matched), never translated.
  const parts = Object.entries(entry.gamePoints ?? {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([ns, pts]) => `${gameNameForNs(ns)} ${pts.toLocaleString()}`);

  return (
    <div className={`${styles.row} ${entry.isSelf ? styles.rowSelf : ''}`}>
      <div className={`${styles.rank} ${isPodium ? styles.rankPodium : ''}`}>
        {medal}
      </div>
      <div className={styles.avatar}>{initial}</div>
      <div className={styles.text}>
        <p className={styles.name}>
          {entry.displayName}
          {entry.isSelf && <span className={styles.youTag}>{t('friends.you')}</span>}
        </p>
        <p className={styles.meta}>
          {parts.length > 0 ? parts.join(' · ') : t('friends.gamesNotPlayed')}
        </p>
      </div>
      <div className={styles.kwhBlock}>
        <span className={styles.kwh}>{entry.totalPoints.toLocaleString()}</span>
        <span className={styles.kwhLabel}>{t('friends.gamePtsLabel')}</span>
      </div>
    </div>
  );
}

function Row({ entry }: { entry: LeaderboardEntry }) {
  const { t } = useTranslation('leaderboard');
  const medal = entry.rank === 1 ? '🥇'
    : entry.rank === 2 ? '🥈'
    : entry.rank === 3 ? '🥉'
    : `#${entry.rank}`;
  const isPodium = entry.rank <= 3;
  const initial = entry.displayName.slice(0, 1).toUpperCase();

  return (
    <div className={`${styles.row} ${entry.isSelf ? styles.rowSelf : ''}`}>
      <div className={`${styles.rank} ${isPodium ? styles.rankPodium : ''}`}>
        {medal}
      </div>
      <div className={styles.avatar}>{initial}</div>
      <div className={styles.text}>
        <p className={styles.name}>
          {entry.displayName}
          {entry.isSelf && <span className={styles.youTag}>{t('friends.you')}</span>}
        </p>
        <p className={styles.meta}>
          {t('friends.sessionCount', { count: entry.sessions })}
          {' · '}
          {t('friends.pointsEarned', { pts: entry.pointsEarned })}
        </p>
      </div>
      <div className={styles.kwhBlock}>
        <span className={styles.kwh}>{entry.kwh.toFixed(1)}</span>
        <span className={styles.kwhLabel}>kWh</span>
      </div>
    </div>
  );
}
